import React, { useState, useEffect } from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useKidsStore } from '../../store/kidsStore';
import { toast } from 'sonner';

type DifficultadKey = 'neurona' | 'sinapsis' | 'cerebro' | 'genio' | 'einstein';

const LEVELS = {
  neurona: { nombre: 'Neurona', icono: '🧠', size: 3, empty: 3 },
  sinapsis: { nombre: 'Sinapsis', icono: '⚡', size: 3, empty: 5 },
  cerebro: { nombre: 'Cerebro', icono: '🧩', size: 4, empty: 8 },
  genio: { nombre: 'Genio', icono: '💡', size: 4, empty: 10 },
  einstein: { nombre: 'Einstein', icono: '🎓', size: 6, empty: 18 }
};

const ANIMAL_WORLDS_3 = [
  ['🐶', '🐷', '🐥'],
  ['🐵', '🐰', '🐯'],
  ['🐠', '🐙', '🦈'],
  ['🦊', '🐻', '🦝']
];

const ANIMAL_WORLDS_4 = [
  ['🐶', '🐷', '🐥', '🐮'],
  ['🐵', '🐰', '🐯', '🦁'],
  ['🐠', '🐙', '🦈', '🐡'],
  ['🦊', '🐻', '🦝', '🐺']
];

const ANIMAL_WORLDS_6 = [
  ['🐶', '🐷', '🐥', '🐮', '🐴', '🐑'],
  ['🐵', '🐰', '🐯', '🦁', '🦒', '🐘'],
  ['🐠', '🐙', '🦈', '🐡', '🐬', '🦀'],
  ['🦊', '🐻', '🦝', '🐺', '🦌', '🦫']
];

interface SudokuData {
  puzzle: number[][];
  solution: number[][];
}

export default function SudokuKidsGame() {
  const tenant = useTenant();
  const { selectedKid, registerProgress, registerPlayTime } = useKidsStore();

  const [nivel, setNivel] = useState<DifficultadKey>('neurona');
  const config = LEVELS[nivel];

  const [sudokuData, setSudokuData] = useState<SudokuData | null>(null);
  const [currentGrid, setCurrentGrid] = useState<number[][]>([]);
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);
  const [selectedSymbol, setSelectedSymbol] = useState<number | null>(null);
  const [symbols, setSymbols] = useState<(string | number)[]>([]);
  const [useNumbers, setUseNumbers] = useState(false);
  const [history, setHistory] = useState<number[][][]>([]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [seconds, setSeconds] = useState(0);
  const [gameStarted, setGameStarted] = useState(false);
  const [gameWon, setGameWon] = useState(false);
  const [wrongCells, setWrongCells] = useState<Set<string>>(new Set());
  const [showRules, setShowRules] = useState(false);

  useEffect(() => {
    startNewGame();
  }, [nivel]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (gameStarted && !gameWon) {
      interval = setInterval(() => setSeconds(s => s + 1), 1000);
    }
    return () => clearInterval(interval);
  }, [gameStarted, gameWon]);

  const generateSolution = (size: number): number[][] => {
    const grid: number[][] = Array(size).fill(0).map(() => Array(size).fill(0));
    const nums = Array.from({ length: size }, (_, i) => i + 1);

    for (let i = nums.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [nums[i], nums[j]] = [nums[j], nums[i]];
    }

    if (size === 3) {
      grid[0] = nums;
      grid[1] = [nums[2], nums[0], nums[1]];
      grid[2] = [nums[1], nums[2], nums[0]];
    } else if (size === 4) {
      grid[0] = nums;
      grid[1] = [nums[2], nums[3], nums[0], nums[1]];
      grid[2] = [nums[3], nums[2], nums[1], nums[0]];
      grid[3] = [nums[1], nums[0], nums[3], nums[2]];
    } else if (size === 6) {
      grid[0] = nums;
      grid[1] = [nums[5], nums[0], nums[1], nums[2], nums[3], nums[4]];
      grid[2] = [nums[4], nums[5], nums[0], nums[1], nums[2], nums[3]];
      grid[3] = [nums[3], nums[4], nums[5], nums[0], nums[1], nums[2]];
      grid[4] = [nums[2], nums[3], nums[4], nums[5], nums[0], nums[1]];
      grid[5] = [nums[1], nums[2], nums[3], nums[4], nums[5], nums[0]];
    }

    return grid;
  };

  const createPuzzle = (solution: number[][], cellsToRemove: number): number[][] => {
    const puzzle = solution.map(row => [...row]);
    const size = solution.length;

    const positions: [number, number][] = [];
    for (let i = 0; i < size; i++) {
      for (let j = 0; j < size; j++) {
        positions.push([i, j]);
      }
    }

    for (let i = positions.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [positions[i], positions[j]] = [positions[j], positions[i]];
    }

    for (let i = 0; i < Math.min(cellsToRemove, positions.length); i++) {
      const [row, col] = positions[i];
      puzzle[row][col] = 0;
    }

    return puzzle;
  };

  const startNewGame = () => {
    const solution = generateSolution(config.size);
    const puzzle = createPuzzle(solution, config.empty);

    const shouldUseNumbers = Math.random() < 0.3;
    setUseNumbers(shouldUseNumbers);

    if (shouldUseNumbers) {
      setSymbols(Array.from({ length: config.size }, (_, i) => i + 1));
    } else {
      let worlds;
      if (config.size === 3) worlds = ANIMAL_WORLDS_3;
      else if (config.size === 4) worlds = ANIMAL_WORLDS_4;
      else worlds = ANIMAL_WORLDS_6;

      const randomWorld = worlds[Math.floor(Math.random() * worlds.length)];
      setSymbols(randomWorld);
    }

    setSudokuData({ puzzle, solution });
    setCurrentGrid(puzzle.map(row => [...row]));
    setHistory([puzzle.map(row => [...row])]);
    setHistoryIndex(0);
    setSelectedCell(null);
    setSelectedSymbol(null);
    setGameStarted(false);
    setGameWon(false);
    setSeconds(0);
    setWrongCells(new Set());
  };

  const handleCellClick = (row: number, col: number) => {
    if (!sudokuData || gameWon) return;
    if (sudokuData.puzzle[row][col] !== 0) return;
    if (!gameStarted) setGameStarted(true);

    setSelectedCell({ row, col });

    if (selectedSymbol !== null) {
      placeSymbol(row, col, selectedSymbol);
    }
  };

  const handleSymbolClick = (value: number) => {
    if (gameWon) return;
    if (!gameStarted) setGameStarted(true);

    setSelectedSymbol(value);

    if (selectedCell !== null) {
      placeSymbol(selectedCell.row, selectedCell.col, value);
    }
  };

  const placeSymbol = (row: number, col: number, value: number) => {
    if (!sudokuData) return;

    const newGrid = currentGrid.map(r => [...r]);
    newGrid[row][col] = value;

    const isCorrect = value === sudokuData.solution[row][col];

    if (isCorrect) {
      setCurrentGrid(newGrid);

      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(newGrid.map(r => [...r]));
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);

      setTimeout(() => {
        if (checkPuzzleComplete(newGrid)) {
          setGameWon(true);
          saveProgress();
        }
      }, 500);
    } else {
      const cellKey = `${row}-${col}`;
      setWrongCells(prev => new Set(prev).add(cellKey));
      setCurrentGrid(newGrid);

      setTimeout(() => {
        const resetGrid = currentGrid.map(r => [...r]);
        resetGrid[row][col] = 0;
        setCurrentGrid(resetGrid);
        setWrongCells(prev => {
          const next = new Set(prev);
          next.delete(cellKey);
          return next;
        });
      }, 1000);
    }

    setSelectedCell(null);
    setSelectedSymbol(null);
  };

  const checkPuzzleComplete = (grid: number[][]): boolean => {
    if (!sudokuData) return false;

    for (let i = 0; i < config.size; i++) {
      for (let j = 0; j < config.size; j++) {
        if (grid[i][j] === 0 || grid[i][j] !== sudokuData.solution[i][j]) {
          return false;
        }
      }
    }
    return true;
  };

  const saveProgress = async () => {
    if (!selectedKid) return;

    try {
      await registerProgress(selectedKid.id, {
        juego_id: null,
        tipo_nivel: 'sudoku-kids',
        nivel_id: Object.keys(LEVELS).indexOf(nivel) + 1,
        tablero_id: null,
        tiempo: seconds,
        movimientos: null
      });

      const minutosJugados = Math.ceil(seconds / 60);
      await registerPlayTime(selectedKid.id, minutosJugados);

      toast.success('¡Progreso guardado! 🎉');
    } catch (error) {
      console.error('Error al guardar:', error);
    }
  };

  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setCurrentGrid(history[historyIndex - 1].map(row => [...row]));
    }
  };

  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setCurrentGrid(history[historyIndex + 1].map(row => [...row]));
    }
  };

  const handleRestart = () => {
    if (!sudokuData) return;
    setCurrentGrid(sudokuData.puzzle.map(row => [...row]));
    setHistory([sudokuData.puzzle.map(row => [...row])]);
    setHistoryIndex(0);
    setSelectedCell(null);
    setSelectedSymbol(null);
    setSeconds(0);
    setGameStarted(false);
    setGameWon(false);
    setWrongCells(new Set());
  };

  const getSymbolDisplay = (value: number) => {
    if (value === 0) return '';
    return symbols[value - 1];
  };

  const getCellClass = (row: number, col: number) => {
    const cellKey = `${row}-${col}`;
    const isPrefilled = sudokuData?.puzzle[row][col] !== 0;
    const isWrong = wrongCells.has(cellKey);
    return `
      aspect-square flex items-center justify-center cursor-pointer transition-all
      ${config.size === 3 ? 'text-4xl sm:text-5xl' : config.size === 4 ? 'text-3xl sm:text-4xl' : 'text-2xl sm:text-3xl'}
      ${isPrefilled ? 'cursor-not-allowed' : ''}
      ${isWrong ? 'animate-shake' : ''}
    `;
  };

  const getCellStyle = (row: number, col: number): React.CSSProperties => {
    const cellKey = `${row}-${col}`;
    const isPrefilled = sudokuData?.puzzle[row][col] !== 0;
    const isSelected = selectedCell?.row === row && selectedCell?.col === col;
    const isWrong = wrongCells.has(cellKey);
    const isCorrect = !isPrefilled && currentGrid[row][col] !== 0 && !isWrong;
    if (isWrong) return { backgroundColor: '#fecaca' };
    if (isSelected) return { backgroundColor: '#bfdbfe' };
    if (isCorrect) return { backgroundColor: '#bbf7d0' };
    if (isPrefilled) return { backgroundColor: '#fde68a', border: '2px solid #b45309' };
    return { backgroundColor: '#ffffff' };
  };

  return (
    <div
      className="h-full flex flex-col"
      style={{ background: tenant.theme.colors.gradients.main, fontFamily: tenant.theme.fonts.body.family }}
    >
      {/* Top bar */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🐾 Sudoku Kids</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />
        <div className="flex gap-1 flex-wrap">
          {Object.entries(LEVELS).map(([key, level]) => (
            <button
              key={key}
              onClick={() => setNivel(key as DifficultadKey)}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${
                nivel === key ? 'bg-white' : 'bg-white/20 text-white hover:bg-white/30'
              }`}
              style={nivel === key ? { color: tenant.theme.colors.primary } : {}}
            >
              {level.icono} {level.nombre}
            </button>
          ))}
        </div>
        <div className="flex-1" />
        <span className="text-white text-xs font-semibold">
          {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
        </span>
        {/* Rules */}
        <button
          onClick={() => setShowRules(!showRules)}
          title="Reglas"
          className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all text-sm font-bold"
        >
          📋
        </button>
        {/* Undo */}
        <button onClick={handleUndo} disabled={historyIndex === 0} title="Deshacer"
          className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
          </svg>
        </button>
        {/* Redo */}
        <button onClick={handleRedo} disabled={historyIndex === history.length - 1} title="Rehacer"
          className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
          </svg>
        </button>
        {/* Restart */}
        <button onClick={handleRestart} title="Reiniciar"
          className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
        {/* New game */}
        <button onClick={startNewGame} title="Nuevo Tablero"
          className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
          <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </button>
      </div>

      {/* Rules panel */}
      {showRules && (
        <div className="bg-white/90 px-4 py-3 text-sm text-gray-800">
          <p className="font-bold mb-1">📋 Reglas de Sudoku Kids</p>
          <p><strong>Objetivo:</strong> Llena las casillas vacías. Cada fila y columna debe tener todos los símbolos diferentes.</p>
          <p className="mt-1"><strong>Cómo jugar:</strong> Haz clic en una casilla vacía, luego selecciona un símbolo. Verde = correcto, Rojo = incorrecto.</p>
        </div>
      )}

      {/* Board */}
      <div className="flex-1 flex flex-col items-center justify-center p-3 gap-4">
        {sudokuData && (
          <>
            <div className="max-w-lg w-full mx-auto">
              <div
                className="grid gap-1 bg-gray-800 border-3 border-gray-800 rounded-xl overflow-hidden"
                style={{ gridTemplateColumns: `repeat(${config.size}, 1fr)` }}
              >
                {currentGrid.map((row, rowIdx) =>
                  row.map((value, colIdx) => (
                    <div
                      key={`${rowIdx}-${colIdx}`}
                      className={getCellClass(rowIdx, colIdx)}
                      style={getCellStyle(rowIdx, colIdx)}
                      onClick={() => handleCellClick(rowIdx, colIdx)}
                    >
                      {getSymbolDisplay(value)}
                    </div>
                  ))
                )}
              </div>
            </div>

            {!gameWon && (
              <div className="flex gap-3 justify-center flex-wrap">
                {symbols.map((symbol, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSymbolClick(idx + 1)}
                    className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl border-3 flex items-center justify-center text-3xl sm:text-4xl transition-all hover:scale-110 ${
                      selectedSymbol === idx + 1 ? 'border-blue-500 bg-blue-100 scale-105' : 'border-white/50 bg-white/20'
                    }`}
                  >
                    {symbol}
                  </button>
                ))}
              </div>
            )}

            <p className="text-center text-white/80 italic text-sm">
              💡 {useNumbers ? 'Esta vez con números!' : 'Completa con los animales'}
            </p>
          </>
        )}

        {gameWon && (
          <div className="text-center bg-white/20 backdrop-blur-sm rounded-2xl p-8">
            <div className="text-6xl mb-4">🎉</div>
            <h2 className="text-3xl font-bold mb-4 text-white">¡Felicidades!</h2>
            <p className="text-xl text-white/80 mb-4">
              Completaste el Sudoku en {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, '0')}
            </p>
            <button
              onClick={startNewGame}
              className="px-8 py-4 rounded-2xl font-bold text-lg shadow-lg text-white"
              style={{ background: tenant.theme.colors.primary }}
            >
              ✨ Nuevo Tablero
            </button>
          </div>
        )}
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        .animate-shake {
          animation: shake 0.5s;
        }
      `}</style>
    </div>
  );
}
